import React, { useState } from "react";
import { Loader2, Mail, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n";

/** Een verkoop of afrekening is pas afgewerkt zodra de factuur afgedrukt of gemaild is.
 *  Beide mag ook. Afdrukken kan achteraf altijd opnieuw via Facturen. */
export default function InvoiceDoneDialog({ open, title, number, email, onFinish }) {
  const { t } = useLang();
  const [pickPrint, setPickPrint] = useState(true);
  const [pickMail, setPickMail] = useState(false);
  const [busy, setBusy] = useState(false);

  const canMail = !!email;
  const finish = async () => {
    setBusy(true);
    try {
      await onFinish({ print: pickPrint, mail: pickMail && canMail });
    } finally { setBusy(false); }
  };

  const Choice = ({ on, set, icon: Icon, label, hint, disabled, testid }) => (
    <button type="button" onClick={() => set(!on)} disabled={disabled} data-testid={testid}
      className={`flex-1 flex flex-col items-start gap-0.5 rounded-xl border-2 px-3 py-2.5 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        on && !disabled ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
      <span className="flex items-center gap-2 font-semibold text-sm"><Icon className="h-4 w-4" />{label}</span>
      <span className="text-[11px] leading-tight text-muted-foreground break-all">{hint}</span>
    </button>
  );

  return (
    <Dialog open={open}>
      <DialogContent data-testid="invoice-done-dialog" className="sm:max-w-lg p-5" hideClose>
        <DialogHeader className="space-y-0.5">
          <DialogTitle className="font-display text-lg">{title || t("invoice_finish")}</DialogTitle>
          {number && <p className="font-mono text-xs text-primary" data-testid="invoice-done-number">{number}</p>}
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("invoice_finish_hint")}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Choice on={pickPrint} set={setPickPrint} icon={Printer} label={t("print")}
              hint={t("invoice_finish_print_hint")} testid="invoice-done-print" />
            <Choice on={pickMail && canMail} set={setPickMail} icon={Mail} label={t("send_email")}
              hint={canMail ? email : t("invoice_finish_no_email")} disabled={!canMail} testid="invoice-done-mail" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={finish} disabled={busy || (!pickPrint && !(pickMail && canMail))}
            data-testid="invoice-done-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("invoice_finish_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
